import {
  DEFAULT_GANTT_DISPLAY_RANGE_MODE,
  GanttDisplayOptions,
  GanttDisplayRangeMode,
  GanttParameterVO,
  ScheduledTerm,
} from '../domain/value-object/GanttParameterVO'

export type RootGanttParameterLoad = {
  rootProjectPlanUuid: string
  isSaved: boolean
}

export type GanttParameterState = {
  ganttParameter?: GanttParameterVO
  projectGanttParameter?: GanttParameterVO
  isProjectGanttParameterSaved?: boolean
  /** プロジェクト単位の表示設定の取得に失敗し、保存内容がわからない */
  isProjectGanttParameterUnknown?: boolean
  rootGanttParameterLoad?: RootGanttParameterLoad
  /** 利用者×プロジェクトの表示期間の動作。undefined は未取得 */
  displayRangeMode?: GanttDisplayRangeMode
}

/**
 * ui_states に保存したプロジェクト単位の表示設定。
 * ganttParameter は利用者の表示に由来する単位と表示期間を含む値で、これがあるときだけ保存済みとして扱う。
 * displayOptions は単位と表示期間が決まる前に保存した表示項目である。
 */
export type SavedProjectGanttParameter = {
  ganttParameter?: GanttParameterVO
  displayOptions?: GanttDisplayOptions
}

/** プロジェクト単位の表示設定として保存する内容。単位と表示期間が決まっていなければ表示項目だけを保存する */
export type ProjectGanttParameterToSave =
  | { kind: 'parameter'; parameter: GanttParameterVO }
  | { kind: 'displayOptions'; displayOptions: GanttDisplayOptions }

export const EMPTY_GANTT_PARAMETER_STATE: GanttParameterState = {
  ganttParameter: undefined,
  projectGanttParameter: undefined,
  isProjectGanttParameterSaved: undefined,
  isProjectGanttParameterUnknown: undefined,
  rootGanttParameterLoad: undefined,
  displayRangeMode: undefined,
}

/**
 * プロジェクトを受け取ったときの state。
 * 表示設定と表示期間の動作は画面がプロジェクトの切り替わりのときだけ読み込むため、同じプロジェクトを受け取り直したときは読み込み済みの値を保つ。
 */
export const applyReceivedProjectToGanttParameterState = <
  S extends GanttParameterState,
>(
  state: S,
  currentProjectUuid: string | undefined,
  receivedProjectUuid: string | undefined
): S =>
  !!receivedProjectUuid && receivedProjectUuid === currentProjectUuid
    ? state
    : { ...state, ...EMPTY_GANTT_PARAMETER_STATE }

const rangeModeOf = (state: GanttParameterState): GanttDisplayRangeMode =>
  state.displayRangeMode ?? DEFAULT_GANTT_DISPLAY_RANGE_MODE

export const applyGanttDisplayRangeMode = <S extends GanttParameterState>(
  state: S,
  mode: GanttDisplayRangeMode
): S => ({ ...state, displayRangeMode: mode })

const withDisplayOptionsOf = (
  parameter: GanttParameterVO,
  source: GanttParameterVO
): GanttParameterVO =>
  new GanttParameterVO(
    parameter.unit,
    parameter.startDay,
    parameter.range,
    source.displayOptions
  )

export const applyChangedGanttParameter = <S extends GanttParameterState>(
  state: S,
  changed: GanttParameterVO,
  rootProjectPlanUuid: string | undefined
): S => {
  if (!rootProjectPlanUuid) {
    return {
      ...state,
      ganttParameter: changed,
      projectGanttParameter: changed,
      isProjectGanttParameterSaved: true,
      isProjectGanttParameterUnknown: false,
    }
  }
  const base = state.projectGanttParameter
  const rootGanttParameterLoad = { rootProjectPlanUuid, isSaved: true }
  if (!base) {
    return { ...state, ganttParameter: changed, rootGanttParameterLoad }
  }
  return {
    ...state,
    ganttParameter: changed,
    rootGanttParameterLoad,
    projectGanttParameter: withDisplayOptionsOf(base, changed),
  }
}

export const applyReceivedProjectGanttParameter = <
  S extends GanttParameterState,
>(
  state: S,
  saved: SavedProjectGanttParameter
): S => {
  const defaultValue = GanttParameterVO.defaultValue()
  const received =
    saved.ganttParameter ??
    new GanttParameterVO(
      defaultValue.unit,
      defaultValue.startDay,
      defaultValue.range,
      saved.displayOptions
    )
  const isRememberRange = rangeModeOf(state) === GanttDisplayRangeMode.REMEMBER
  // ルートの保存値を表示しているときは、ほかの画面がプロジェクト単位の表示設定を読み直しても、ルートの単位と表示期間を保つ
  const isShowingSavedRoot =
    isRememberRange && !!state.rootGanttParameterLoad?.isSaved
  return {
    ...state,
    ganttParameter:
      isShowingSavedRoot && state.ganttParameter
        ? withDisplayOptionsOf(state.ganttParameter, received)
        : received,
    projectGanttParameter: received,
    isProjectGanttParameterSaved: !!saved.ganttParameter,
    isProjectGanttParameterUnknown: false,
    rootGanttParameterLoad: isRememberRange
      ? state.rootGanttParameterLoad
      : undefined,
  }
}

export const applyReceivedRootGanttParameter = <S extends GanttParameterState>(
  state: S,
  received: GanttParameterVO | undefined,
  rootProjectPlanUuid: string | undefined
): S => {
  const base = state.projectGanttParameter
  // ルートの表示設定は、プロジェクト単位の表示設定の読み込み後に読む（useGanttParameterRootUuid）
  if (!base) return state
  if (
    !rootProjectPlanUuid ||
    rangeModeOf(state) !== GanttDisplayRangeMode.REMEMBER
  ) {
    return { ...state, ganttParameter: base, rootGanttParameterLoad: undefined }
  }
  return {
    ...state,
    ganttParameter: received ? withDisplayOptionsOf(received, base) : base,
    rootGanttParameterLoad: { rootProjectPlanUuid, isSaved: !!received },
  }
}

/**
 * ui_states の応答から表示設定の値を取り出す。
 * ui_states の取得に失敗すると key のない空の応答になり、未保存と区別できないため、key がなければ例外を投げる。
 */
export const readGanttParameterUiStateValue = (json: {
  key?: string
  value?: string | null
}): string | undefined => {
  if (!json.key) {
    throw new Error('Failed to fetch gantt parameter ui state')
  }
  return json.value ?? undefined
}

/**
 * ルートの表示設定の取得に失敗したときの state。
 * 保存内容がわからないため、WBSごとのときは初回表示の合わせで保存済みの値を上書きしないよう、保存済みとして扱う。
 * 表示にはプロジェクト単位の表示設定を使う。
 */
export const applyFailedRootGanttParameter = <S extends GanttParameterState>(
  state: S,
  rootProjectPlanUuid: string
): S => {
  const applied = applyReceivedRootGanttParameter(
    state,
    undefined,
    rootProjectPlanUuid
  )
  return applied.rootGanttParameterLoad
    ? {
        ...applied,
        rootGanttParameterLoad: { rootProjectPlanUuid, isSaved: true },
      }
    : applied
}

export const applySavedProjectGanttParameter = <S extends GanttParameterState>(
  state: S,
  saved: GanttParameterVO
): S => ({
  ...state,
  projectGanttParameter: saved,
  isProjectGanttParameterSaved: true,
})

/**
 * プロジェクト単位の表示設定の取得に失敗したときの state。
 * 保存内容がわからないため、初回表示の合わせで保存済みの値を上書きしないよう、保存済みとして扱う。
 * ルートの保存に合わせたプロジェクト単位の表示設定の保存も、利用者がプロジェクト単位の表示設定を変えるまで止める。
 */
export const applyFailedProjectGanttParameter = <S extends GanttParameterState>(
  state: S
): S => {
  const current = state.projectGanttParameter ?? GanttParameterVO.defaultValue()
  return {
    ...state,
    ganttParameter: state.ganttParameter ?? current,
    projectGanttParameter: current,
    isProjectGanttParameterSaved: true,
    isProjectGanttParameterUnknown: true,
  }
}

/**
 * ルートの表示設定の保存に合わせて保存する、プロジェクト単位の表示設定。
 * 未保存のプロジェクト単位の表示設定は既定の期間なので、プロジェクトをルートにしたときの初回表示と同じ規則で予定期間に合わせてから保存する。
 * 予定期間がなければ表示項目だけを保存し、予定期間が入力された後の初回表示で合わせられるよう未保存のまま保つ。
 */
export const resolveProjectGanttParameterToSave = (
  state: GanttParameterState,
  projectScheduledDate: ScheduledTerm | undefined,
  fiscalStartMonth: number
): ProjectGanttParameterToSave | undefined => {
  const base = state.projectGanttParameter
  if (!base || state.isProjectGanttParameterUnknown) return undefined
  if (state.isProjectGanttParameterSaved) {
    return { kind: 'parameter', parameter: base }
  }
  const period = GanttParameterVO.findFitPeriod([
    GanttParameterVO.periodOf(projectScheduledDate),
  ])
  return period
    ? { kind: 'parameter', parameter: base.fitTo(period, fiscalStartMonth) }
    : { kind: 'displayOptions', displayOptions: base.displayOptions }
}
